// Pin a `--app-height` CSS variable to the real *visible* viewport height.
//
// Mobile browsers resolve `100vh`/`100dvh` against the layout viewport, which
// can be taller than the visible area (browser chrome, dynamic toolbars). That
// let the app shell grow past the screen, so users could scroll the sticky
// bottom nav up into empty background — most visibly on iOS Safari at load,
// when the toolbar is expanded and the visible area is at its smallest.
//
// `window.innerHeight` is the wrong metric here: on iOS Safari it reports the
// *layout* viewport (toolbar-collapsed, i.e. the larger height) even while the
// expanded toolbar is on screen, so feeding it to `min-height` reproduces the
// very gap we're trying to remove. `window.visualViewport.height` is the actual
// visible height and updates as the toolbar shows/hides, so we prefer it and
// fall back to `innerHeight` only where `visualViewport` is unavailable (old
// WebViews).
const trackAppHeight = (): void => {
  const vv = window.visualViewport;

  // The on-screen keyboard shrinks `visualViewport.height`; locking the shell to
  // that would squash the layout while a field is focused. Skip updates in that
  // case — a `resize` fires again on blur and restores the full height.
  const isEditableFocused = () => {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    return (
      el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.isContentEditable
    );
  };

  let frame = 0;
  const setHeight = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      if (isEditableFocused()) return;
      const h = vv?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        '--app-height',
        `${Math.round(h)}px`,
      );
    });
  };

  setHeight();

  if (vv) {
    // `scroll` fires as the toolbar animates in/out; `resize` on rotate/zoom.
    vv.addEventListener('resize', setHeight);
    vv.addEventListener('scroll', setHeight);
  }
  window.addEventListener('resize', setHeight);
  window.addEventListener('orientationchange', setHeight);
  // Restored from the bfcache (back/forward) — re-measure, the viewport may
  // have changed while the page was frozen.
  window.addEventListener('pageshow', setHeight);
};

export default trackAppHeight;
